import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ExternalLink, Plus, Loader2 } from "lucide-react";

export default function AiOpportunityFinderDialog({ open, onOpenChange, onAdded }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [addedTitles, setAddedTitles] = useState([]);

  const findOpportunities = async () => {
    setLoading(true);
    setSuggestions([]);
    try {
      const res = await api.post("/opportunities/ai-suggest");
      setSuggestions(res.data.suggestions || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const addSuggestion = async (s) => {
    try {
      await api.post("/opportunities", { title: s.title, category: s.category, source: s.source, notes: s.notes, external_link: s.external_link });
      toast.success(`Added "${s.title}" to Opportunities`);
      setAddedTitles((prev) => [...prev, s.title]);
      onAdded?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setSuggestions([]); setAddedTitles([]); } }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-opportunity-finder-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#76C121]" /> Find New Opportunities with AI</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#4B5563]">
          ChatGPT (GPT-5.4 Mini) suggests ESG-relevant workshops, grants, competitions, and collaborations. These are AI-generated starting points — please verify details independently before applying.
        </p>
        <Button onClick={findOpportunities} disabled={loading} data-testid="ai-opportunity-generate-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white w-fit">
          {loading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Searching...</> : <><Sparkles className="h-4 w-4 mr-1.5" /> {suggestions.length ? "Find More" : "Find Opportunities"}</>}
        </Button>
        <div className="space-y-3 mt-2">
          {suggestions.map((s, i) => (
            <div key={i} data-testid={`ai-suggestion-card-${i}`} className="border border-[#E2E8F0] rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1C1C1E] flex items-center gap-1.5">
                    {s.title}
                    {s.external_link && (
                      <a href={s.external_link} target="_blank" rel="noreferrer" data-testid={`ai-suggestion-link-${i}`}><ExternalLink className="h-3.5 w-3.5 text-[#00823B]" /></a>
                    )}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <StatusBadge value={s.category} />
                    <span className="text-xs text-[#4B5563]">via {s.source}</span>
                  </div>
                  <p className="text-xs text-[#4B5563] mt-2">{s.notes}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={addedTitles.includes(s.title)}
                  onClick={() => addSuggestion(s)}
                  data-testid={`ai-suggestion-add-button-${i}`}
                  className="shrink-0"
                >
                  {addedTitles.includes(s.title) ? "Added" : <><Plus className="h-3.5 w-3.5 mr-1" /> Add</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
